import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import PostCard from '../components/PostCard';
import { Loader2, Code2, MessageSquareCode, Sparkles, ArrowRight } from 'lucide-react';
import SEO, { buildWebsiteJsonLd } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/posts`);
        setPosts(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching posts:', err);
        setError('Failed to load posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Featured: AI Ethics posts marked as featured (latest first)
  const featuredPosts = posts
    .filter(post => post.featured && post.category === 'AI Ethics')
    .slice(0, 2);
  
  // If not enough AI Ethics featured posts, add Machine Learning featured posts
  const mlFeatured = posts
    .filter(post => post.featured && post.category === 'Machine Learning')
    .slice(0, 2 - featuredPosts.length);
  
  const allFeatured = [...featuredPosts, ...mlFeatured].slice(0, 2);
  
  // Recent: Mix of AI Ethics and Machine Learning (non-featured, latest first)
  const recentAIEthics = posts
    .filter(post => !post.featured && post.category === 'AI Ethics')
    .slice(0, 2);
  
  const recentML = posts
    .filter(post => !post.featured && post.category === 'Machine Learning')
    .slice(0, 2);
  
  const recentBias = posts
    .filter(post => !post.featured && post.category === 'Bias & Fairness')
    .slice(0, 1);
  
  // Combine and take top 3
  const recentPosts = [...recentAIEthics, ...recentML, ...recentBias]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="home-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="home-error">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO
        title="AI Ethics for Teens, Students & Curious Minds"
        description="Bite-sized AI ethics lessons for high schoolers, students, and the AI-curious. Learn responsible AI, machine learning, bias, and the latest models — written by Riddhi Tikoo, a 10th-grade AI researcher."
        path="/"
        jsonLd={buildWebsiteJsonLd()}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-light mb-6 tracking-tight leading-tight">
            Exploring AI Ethics
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            A teenager's journey through the complex landscape of artificial 
            intelligence, ethics, and responsible technology development.
          </p>
        </div>
      </section>

      {/* Free Guides spotlight (lead magnet) */}
      <section className="py-12 px-6" data-testid="home-guides-spotlight">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-semibold tracking-wider uppercase mb-2">Free · Updated weekly</span>
              <h2 className="text-3xl font-light">Free guides for curious builders</h2>
            </div>
            <Link to="/guides" className="text-sm text-gray-700 hover:text-black inline-flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { slug: 'claude-code-tips', icon: Code2, title: 'Claude Code: 25 Tips & Tricks', subtitle: 'Pro patterns from real coding sessions.', color: 'from-orange-500 to-amber-500' },
              { slug: 'chatgpt-prompting', icon: MessageSquareCode, title: 'ChatGPT Prompting Playbook', subtitle: '30 proven patterns that get 10x better answers.', color: 'from-emerald-500 to-teal-500' },
              { slug: 'hot-ai-models', icon: Sparkles, title: 'Hot AI Models This Month', subtitle: 'New models worth trying — explained simply.', color: 'from-violet-500 to-fuchsia-500' },
            ].map(({ slug, icon: Icon, title, subtitle, color }) => (
              <Link
                key={slug}
                to={`/guides/${slug}`}
                className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-xl"
                data-testid={`home-magnet-${slug}`}
              >
                <div className={`absolute -right-8 -top-8 w-32 h-32 rounded-full bg-gradient-to-br ${color} opacity-10 group-hover:opacity-20 transition-opacity`} />
                <div className={`relative inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br ${color} text-white mb-4`}>
                  <Icon size={20} />
                </div>
                <h3 className="relative text-lg font-medium text-gray-900 mb-1.5 leading-snug">{title}</h3>
                <p className="relative text-sm text-gray-600 leading-relaxed">{subtitle}</p>
                <span className="relative inline-flex items-center gap-1 text-sm font-medium text-gray-900 mt-4">
                  Unlock free <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts - AI Ethics & Machine Learning */}
      {allFeatured.length > 0 && (
        <section className="py-16 px-6 bg-gray-50" data-testid="featured-posts">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-light mb-12">Featured Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {allFeatured.map(post => (
                <PostCard key={post.slug} post={post} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Posts - Mixed Categories */}
      {recentPosts.length > 0 && (
        <section className="py-24 px-6" data-testid="recent-posts">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-light mb-12">Recent Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {recentPosts.map(post => (
                <PostCard key={post.slug} post={post} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {posts.length === 0 && (
        <section className="py-24 px-6 text-center" data-testid="no-posts">
          <p className="text-xl text-gray-600">No posts available yet. Check back soon!</p>
        </section>
      )}
    </div>
  );
};

export default Home;
